/**
 * The retry policy for a user's requests (the spec section cut, the section export, a model
 * upload's blocks and its complete, a model's files and metadata reads): a request is sent again
 * only after a transient failure, at most {@link TRANSIENT_RETRIES} times by default, like
 * `postProductEvents`. Everything else rejects at once with the request's own error: a refusal
 * (any other 4xx, including a 409 that carries the server's verdict), a 500 unless the caller's
 * {@link RetryPolicy} lists it, a cancellation.
 */

import { isAxiosError, isCancel } from "axios";
import pRetry from "p-retry";

import { responseHeader } from "../utils/responseHeaders";

export const TRANSIENT_RETRIES = 2;

const TRANSIENT_STATUSES: ReadonlySet<number> = new Set([429, 502, 503, 504]);
const BASE_DELAY_MS = 1_000;
/** A longer Retry-After is waited only this long: the user is watching the download. */
const MAX_RETRY_AFTER_MS = 10_000;

/** How a caller's requests are sent again; each field defaults to the download policy. */
export interface RetryPolicy {
    /** Attempts after the first. */
    retries?: number;
    /** The answers another attempt may cure (default 429, 502, 503 and 504). */
    statuses?: ReadonlySet<number>;
    /** Ends the wait between attempts and rejects with its reason. */
    signal?: AbortSignal;
}

/** A fetch answered with one of the policy's statuses, its body cancelled: sent again. */
class TransientAnswer extends Error {
    constructor(
        readonly status: number,
        readonly retryAfter: string | null,
    ) {
        super(`Transient answer ${status}`);
        this.name = "TransientAnswer";
    }
}

/** A fetch that got no answer (the network `TypeError` fetch rejects with): sent again. */
class NoAnswer extends Error {
    constructor(readonly failure: TypeError) {
        super(failure.message);
        this.name = "NoAnswer";
    }
}

/**
 * A failure worth sending again: a request that got no response (an axios network error or
 * timeout that was not cancelled, a fetch's `NoAnswer`), or one answered with one of `statuses`.
 */
function isTransientFailure(error: unknown, statuses: ReadonlySet<number>): boolean {
    if (error instanceof NoAnswer) return true;
    if (error instanceof TransientAnswer) return statuses.has(error.status);
    if (!isAxiosError(error) || isCancel(error)) return false;
    const status = error.response?.status;
    return status === undefined || statuses.has(status);
}

function retryAfterMilliseconds(error: unknown): number {
    const value =
        error instanceof TransientAnswer
            ? error.retryAfter
            : isAxiosError(error)
              ? responseHeader(error.response?.headers, "Retry-After")
              : undefined;
    if (!value) return 0;
    const seconds = Number(value);
    const milliseconds = Number.isFinite(seconds)
        ? seconds * 1_000
        : Date.parse(value) - Date.now();
    return Number.isFinite(milliseconds) && milliseconds > 0
        ? Math.min(milliseconds, MAX_RETRY_AFTER_MS)
        : 0;
}

function backoffMilliseconds(retryIndex: number): number {
    return BASE_DELAY_MS * 2 ** retryIndex * (1 + Math.random());
}

/** Resolves after `milliseconds`, or as soon as `signal` aborts. */
function wait(milliseconds: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
        if (signal?.aborted) return resolve();
        const done = () => {
            clearTimeout(timer);
            signal?.removeEventListener("abort", done);
            resolve();
        };
        const timer = setTimeout(done, milliseconds);
        signal?.addEventListener("abort", done);
    });
}

/** Carries an attempt's own error, whatever it is, through p-retry: p-retry rejects a thrown
 *  non-`Error` as a TypeError, and its `AbortError` would wrap one in a new `Error`. */
class FailedAttempt extends Error {
    constructor(readonly failure: unknown) {
        super("Request attempt failed");
        this.name = "FailedAttempt";
    }
}

/**
 * Run `request`, sending it again after a transient failure ({@link isTransientFailure}) up
 * to {@link TRANSIENT_RETRIES} times (or `policy.retries`): after 1–2 s, then 2–4 s, doubling,
 * or the server's Retry-After when that is longer (capped at 10 s). Rejects with the last
 * attempt's own error, or with `policy.signal`'s reason once it aborts.
 */
export async function withTransientRetry<T>(
    request: () => Promise<T>,
    { retries = TRANSIENT_RETRIES, statuses = TRANSIENT_STATUSES, signal }: RetryPolicy = {},
): Promise<T> {
    try {
        return await pRetry(
            async () => {
                try {
                    signal?.throwIfAborted();
                    return await request();
                } catch (failure) {
                    const attempt = new FailedAttempt(failure);
                    throw isTransientFailure(failure, statuses)
                        ? attempt
                        : new pRetry.AbortError(attempt);
                }
            },
            {
                retries,
                // p-retry owns the attempt loop; this hook owns its single delay, so the backoff
                // and a Retry-After combine rather than stack.
                minTimeout: 0,
                maxTimeout: 0,
                onFailedAttempt: async (error) => {
                    if (error.retriesLeft === 0) return;
                    const retryAfter =
                        error instanceof FailedAttempt ? retryAfterMilliseconds(error.failure) : 0;
                    await wait(
                        Math.max(backoffMilliseconds(error.attemptNumber - 1), retryAfter),
                        signal,
                    );
                },
            },
        );
    } catch (error) {
        throw error instanceof FailedAttempt ? error.failure : error;
    }
}

/** How {@link fetchWithTransientRetry} sends a fetch again. */
export interface FetchRetryPolicy extends RetryPolicy {
    /** Lets go of a transient answer's body before the next attempt (default: cancel it). A
     *  failure ends the retries and rejects with its error: a body that could not be let go of
     *  must stay observable, and never starts another attempt. */
    discard?: (response: Response) => Promise<void>;
}

/**
 * {@link withTransientRetry} for a fetch: a transient answer (one of `statuses`) has its body let
 * go of and is asked again, as is a request that got no answer (fetch's network `TypeError`). The
 * last attempt's answer is handed back whatever its status, for the caller to read as it reads any
 * refusal; its network error is thrown unchanged. `send` makes one attempt, and owns everything
 * else about it: its URL, credentials, fences and signal.
 */
export async function fetchWithTransientRetry(
    send: () => Promise<Response>,
    { discard = async (response) => response.body?.cancel(), ...policy }: FetchRetryPolicy = {},
): Promise<Response> {
    const { retries = TRANSIENT_RETRIES, statuses = TRANSIENT_STATUSES } = policy;
    let attempt = 0;
    return withTransientRetry(async () => {
        const last = attempt++ === retries;
        let response: Response;
        try {
            response = await send();
        } catch (error) {
            throw !last && error instanceof TypeError ? new NoAnswer(error) : error;
        }
        if (last || !statuses.has(response.status)) return response;
        await discard(response);
        throw new TransientAnswer(response.status, response.headers.get("Retry-After"));
    }, policy);
}
